/**
 * StickyRightNav — Collapsible accordion sidebar
 * Only shows AFTER hero section (scrollY > hero height)
 * LEFT side positioning to avoid carousel overlap
 * Mobile: auto-hide on scroll down, show on scroll up
 */
import { useState, useEffect, useRef } from 'react';
import { MapPin, Store, Tag, Sparkles, Calendar, Users, Compass, ChevronRight, ChevronLeft } from 'lucide-react';

const SECTIONS = [
  { id: 'foodie-feed', icon: MapPin, label: 'Foodie Feed', color: '#FF6B35' },
  { id: 'near-you', icon: Store, label: 'Near You', color: '#F4C430' },
  { id: 'deals', icon: Tag, label: 'Deals', color: '#EF4444' },
  { id: 'ai-quests', icon: Sparkles, label: 'AI Quests', color: '#8B5CF6' },
  { id: 'events', icon: Calendar, label: 'Events', color: '#3B82F6' },
  { id: 'friends', icon: Users, label: 'Friends', color: '#10B981' },
  { id: 'hidden-gems', icon: Compass, label: 'Gems', color: '#EC4899' },
];

const StickyRightNav = () => {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      // Only show after hero section (approx 600px)
      const pastHero = y > 500;

      if (window.innerWidth < 768) {
        setVisible(pastHero && (y < lastScrollY.current || y < 100));
      } else {
        setVisible(pastHero);
      }
      lastScrollY.current = y;

      // Detect active section
      if (pastHero) {
        for (const s of [...SECTIONS].reverse()) {
          const el = document.getElementById(s.id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 200) { setActiveSection(s.id); break; }
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className={`fixed left-0 top-1/2 -translate-y-1/2 z-40 transition-all duration-300 ${
        visible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
      data-testid="sticky-right-nav"
    >
      <div className="flex items-center">
        {/* Nav items */}
        <div className="bg-white/95 backdrop-blur-lg shadow-xl rounded-r-2xl border border-l-0 border-gray-100 py-2 px-1.5 flex flex-col gap-1">
          {SECTIONS.map(({ id, icon: Icon, label, color }) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`flex items-center gap-2 rounded-xl transition-all duration-200 ${
                  expanded ? 'px-3 py-2' : 'px-2 py-2'
                } ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                title={label}
                data-testid={`sticky-nav-${id}`}
              >
                <div className={`flex-shrink-0 transition-all ${expanded ? 'w-5 h-5' : 'w-2.5 h-2.5 rounded-full'}`}
                  style={expanded ? {} : { backgroundColor: isActive ? color : '#D1D5DB' }}
                >
                  {expanded && <Icon className="w-5 h-5" style={{ color: isActive ? color : '#9CA3AF' }} />}
                </div>
                {expanded && (
                  <span className={`text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive ? 'text-gray-900' : 'text-gray-500'
                  }`}>{label}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Expand/collapse toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-6 h-10 bg-white/90 backdrop-blur-sm shadow-md rounded-r-lg flex items-center justify-center text-gray-400 hover:text-gray-600 border border-l-0 border-gray-200 transition-colors"
          data-testid="sticky-nav-toggle"
        >
          {expanded ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
};

export default StickyRightNav;
